import type {
	AppEventJsonValue,
	AppEventOccurrenceType,
	PublicCommandPayload,
} from "@getuserfeedback/protocol";
import type { TrackOptions } from "@getuserfeedback/protocol/host";
import { assertNoSegmentExternalIdsInValueArgument } from "@getuserfeedback/protocol/host";
import {
	parseCustomerOccurrenceName,
	parseTrackMessageId,
} from "@getuserfeedback/protocol/internal/sdk-event-options";

export type EventProperties = Record<string, AppEventJsonValue>;
export type EventOptions = TrackOptions;

type CustomerOccurrenceCommand = Extract<
	PublicCommandPayload,
	{ kind: "track" }
>;

const occurrenceCallShapes = {
	page: "page(name, properties, { externalIds })",
	screen: "screen(name, properties, { externalIds })",
	track: "track(eventName, properties, { externalIds })",
} as const;

const occurrenceArgumentNames = {
	page: "pageName",
	screen: "screenName",
	track: "eventName",
} as const;

export const buildCustomerOccurrenceCommand = (input: {
	type: AppEventOccurrenceType;
	event: string;
	properties?: EventProperties;
	options?: EventOptions;
}): CustomerOccurrenceCommand => {
	const event = parseCustomerOccurrenceName(
		input.event,
		occurrenceArgumentNames[input.type],
	);
	assertNoSegmentExternalIdsInValueArgument({
		argument: input.properties,
		commandName: input.type,
		valueArgumentName: "properties",
		callShape: occurrenceCallShapes[input.type],
	});
	const messageId = parseTrackMessageId(input.options?.messageId);

	return {
		kind: "track",
		origin: "customer",
		...(input.type === "track" ? {} : { type: input.type }),
		event,
		...(input.properties === undefined ? {} : { properties: input.properties }),
		...(messageId === undefined ? {} : { messageId }),
		...(input.options?.externalIds === undefined
			? {}
			: { context: { externalIds: input.options.externalIds } }),
	};
};
